/**
 * MatchResultsList - Season match results with expandable per-match detail.
 *
 * Each row is a compact result card. Clicking the row slides open a detail
 * panel showing:
 *   - Goalscorers (event_type='goal' or 'penalty_goal', minute)
 *   - Own goals (event_type='own_goal')
 *   - Substitutions made (event_type='substitution', sub_on / sub_off names)
 *   - Yellow cards (event_type='yellow_card')
 *   - Red cards (event_type='red_card')
 *
 * All figures are derived from the same `match.events` feed the app already
 * writes on the pitch — no separate DB fields, no drift between screens.
 */
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Calendar, ChevronDown, Target, Repeat, User, ArrowRight } from 'lucide-react';
import { getMatchResult, getMatchScore, getMatchLabel } from './utils';

function buildPlayerNameMap(players) {
  const map = {};
  (players || []).forEach(p => { if (p?.id) map[p.id] = p.name || 'Unknown'; });
  return map;
}

function extractMatchDetail(match, playerNameMap) {
  const events = (match?.events || []).filter(e => !e?.is_opponent);
  const nameFor = (id, fallback) => (id && playerNameMap[id]) || fallback || 'Unknown';

  // "Subs available" = the bench squad the coach named for this match.
  // Stored in `match.subs` (either dicts with playerId or raw ids). Non-empty
  // entries only. Used to render `N made / M available` in the header.
  const subsAvailable = (match?.subs || []).filter(Boolean).length;

  const goals = [];
  const ownGoals = [];
  const subs = [];
  const yellows = [];
  const reds = [];

  // Substitutions are stored as TWO events per swap in the current writer:
  // a `substitution_out` (player leaving) immediately followed by a
  // `substitution_in` (player coming on). We walk the events in order and
  // pair adjacent out/in with the same minute into a single UI row.
  // We also support the AI-extraction shape (`event_type='substitution'`
  // with sub_off_id/sub_on_id) for forwards-compat.
  let pendingOff = null;

  events.forEach(e => {
    const type = e?.event_type;
    const minute = e?.minute;

    if (type === 'goal' || type === 'penalty_goal') {
      goals.push({
        name: nameFor(e.player_id, e.player_name),
        assist: e.assist_player_id || e.related_player_id
          ? nameFor(e.assist_player_id || e.related_player_id, e.assist_player_name)
          : null,
        minute,
        penalty: type === 'penalty_goal',
      });
    } else if (type === 'own_goal') {
      ownGoals.push({ name: nameFor(e.player_id, e.player_name), minute });
    } else if (type === 'substitution') {
      subs.push({
        off: nameFor(e.sub_off_id, e.sub_off_name),
        on:  nameFor(e.sub_on_id,  e.sub_on_name),
        minute,
      });
    } else if (type === 'substitution_out') {
      pendingOff = {
        name: nameFor(e.player_id, e.player_name),
        minute,
      };
    } else if (type === 'substitution_in') {
      const onName = nameFor(e.player_id, e.player_name);
      if (pendingOff) {
        subs.push({ off: pendingOff.name, on: onName, minute: minute ?? pendingOff.minute });
        pendingOff = null;
      } else {
        subs.push({ off: '\u2014', on: onName, minute });
      }
    } else if (type === 'yellow_card') {
      yellows.push({ name: nameFor(e.player_id, e.player_name), minute });
    } else if (type === 'red_card') {
      reds.push({ name: nameFor(e.player_id, e.player_name), minute });
    }
  });

  // Unpaired `substitution_out` at the end (defensive): still show it.
  if (pendingOff) {
    subs.push({ off: pendingOff.name, on: '\u2014', minute: pendingOff.minute });
  }

  return { goals, ownGoals, subs, subsAvailable, yellows, reds };
}

const minuteLabel = (m) => (m || m === 0) ? `${m}'` : '';

export function MatchResultsList({ completedMatches, players }) {
  const [openId, setOpenId] = useState(null);
  const playerNameMap = buildPlayerNameMap(players);

  if (!completedMatches?.length) return null;

  const getResultBadge = (match) => {
    const result = getMatchResult(match);
    if (result === 'W') return <Badge className="bg-emerald-500 text-white">W</Badge>;
    if (result === 'L') return <Badge className="bg-red-500 text-white">L</Badge>;
    return <Badge className="bg-amber-500 text-white">D</Badge>;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-emerald-600" strokeWidth={1.5} />Match Results
        </CardTitle>
        <p className="text-sm text-gray-500">Tap a match to see goalscorers, subs and cards.</p>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col gap-3">
          {[...completedMatches].reverse().map((match) => {
            const isOpen = openId === match.id;
            const detail = extractMatchDetail(match, playerNameMap);
            const totalEvents = detail.goals.length + detail.ownGoals.length
              + detail.subs.length + detail.subsAvailable + detail.yellows.length + detail.reds.length;

            return (
              <div
                key={match.id}
                className="rounded-xl border border-gray-100 bg-gray-50 overflow-hidden transition-colors"
                data-testid={`match-result-${match.id}`}
              >
                {/* Header row — always visible, click toggles detail */}
                <button
                  type="button"
                  onClick={() => setOpenId(isOpen ? null : match.id)}
                  className="w-full flex items-center justify-between p-3 sm:p-4 text-left hover:bg-gray-100 transition-colors"
                  data-testid={`match-result-toggle-${match.id}`}
                  aria-expanded={isOpen}
                >
                  <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                    {getResultBadge(match)}
                    <div className="min-w-0">
                      <p className="font-medium text-sm sm:text-base text-gray-900 truncate">{match.opponent}</p>
                      <p className="text-xs text-gray-500 truncate">
                        {match.competition} • {match.venue === 'home' ? 'Home' : match.venue === 'away' ? 'Away' : 'Neutral'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0 ml-2">
                    <div className="text-right">
                      <p className="text-lg sm:text-xl font-bold text-gray-900">{getMatchScore(match)}</p>
                      <p className="text-xs text-gray-500">{getMatchLabel(match)}</p>
                    </div>
                    <ChevronDown
                      className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                      strokeWidth={2}
                    />
                  </div>
                </button>

                {/* Slide-in detail panel */}
                <div
                  className={`grid transition-[grid-template-rows] duration-300 ease-out ${
                    isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                  }`}
                  data-testid={`match-result-detail-${match.id}`}
                >
                  <div className="overflow-hidden">
                    <div className="px-3 sm:px-4 pb-4 pt-1 border-t border-gray-200 bg-white">
                      {totalEvents === 0 ? (
                        <p className="text-xs text-gray-400 italic py-2">No events logged for this match.</p>
                      ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-3">
                          {/* Goalscorers */}
                          {(detail.goals.length > 0 || detail.ownGoals.length > 0) && (
                            <DetailBlock
                              icon={<Target className="w-4 h-4 text-emerald-600" strokeWidth={2} />}
                              title={`Goalscorers · ${detail.goals.length + detail.ownGoals.length}`}
                              testid={`match-goals-${match.id}`}
                            >
                              {detail.goals.map((g, i) => (
                                <li key={`g-${i}`} className="text-xs flex items-center gap-1.5">
                                  <span className="font-medium text-gray-900">{g.name}</span>
                                  {g.penalty && <span className="text-[10px] font-bold uppercase text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">Pen</span>}
                                  {g.assist && <span className="text-[10px] text-gray-500">assist: {g.assist}</span>}
                                  <span className="text-gray-400 ml-auto">{minuteLabel(g.minute)}</span>
                                </li>
                              ))}
                              {detail.ownGoals.map((o, i) => (
                                <li key={`og-${i}`} className="text-xs flex items-center gap-1.5">
                                  <span className="font-medium text-gray-900">{o.name}</span>
                                  <span className="text-[10px] font-bold uppercase text-red-700 bg-red-50 px-1.5 py-0.5 rounded">OG</span>
                                  <span className="text-gray-400 ml-auto">{minuteLabel(o.minute)}</span>
                                </li>
                              ))}
                            </DetailBlock>
                          )}

                          {/* Substitutions */}
                          {(detail.subs.length > 0 || detail.subsAvailable > 0) && (
                            <DetailBlock
                              icon={<Repeat className="w-4 h-4 text-indigo-600" strokeWidth={2} />}
                              title={`Subs made · ${detail.subs.length}${detail.subsAvailable > 0 ? ` / ${detail.subsAvailable}` : ''}`}
                              testid={`match-subs-${match.id}`}
                            >
                              {detail.subs.length === 0 && (
                                <li className="text-xs text-gray-400 italic">
                                  None used from {detail.subsAvailable} available
                                </li>
                              )}
                              {detail.subs.map((s, i) => (
                                <li key={`s-${i}`} className="text-xs flex items-center gap-1.5">
                                  <span className="text-red-600">{s.off}</span>
                                  <ArrowRight className="w-3 h-3 text-gray-400 shrink-0" strokeWidth={2} />
                                  <span className="font-medium text-gray-900">{s.on}</span>
                                  <span className="text-gray-400 ml-auto">{minuteLabel(s.minute)}</span>
                                </li>
                              ))}
                            </DetailBlock>
                          )}

                          {/* Yellow cards */}
                          {detail.yellows.length > 0 && (
                            <DetailBlock
                              icon={<div className="w-3 h-4 rounded-sm bg-yellow-400" />}
                              title={`Yellow cards · ${detail.yellows.length}`}
                              testid={`match-yellows-${match.id}`}
                            >
                              {detail.yellows.map((y, i) => (
                                <li key={`y-${i}`} className="text-xs flex items-center gap-1.5">
                                  <User className="w-3 h-3 text-gray-400 shrink-0" strokeWidth={2} />
                                  <span className="font-medium text-gray-900">{y.name}</span>
                                  <span className="text-gray-400 ml-auto">{minuteLabel(y.minute)}</span>
                                </li>
                              ))}
                            </DetailBlock>
                          )}

                          {/* Red cards */}
                          {detail.reds.length > 0 && (
                            <DetailBlock
                              icon={<div className="w-3 h-4 rounded-sm bg-red-500" />}
                              title={`Red cards · ${detail.reds.length}`}
                              testid={`match-reds-${match.id}`}
                            >
                              {detail.reds.map((r, i) => (
                                <li key={`r-${i}`} className="text-xs flex items-center gap-1.5">
                                  <User className="w-3 h-3 text-gray-400 shrink-0" strokeWidth={2} />
                                  <span className="font-medium text-gray-900">{r.name}</span>
                                  <span className="text-gray-400 ml-auto">{minuteLabel(r.minute)}</span>
                                </li>
                              ))}
                            </DetailBlock>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function DetailBlock({ icon, title, children, testid }) {
  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-1.5 mb-1.5">
        {icon}
        <span className="text-[11px] font-bold uppercase tracking-wider text-gray-500">{title}</span>
      </div>
      <ul className="space-y-1 pl-1">{children}</ul>
    </div>
  );
}
